import { withRouter } from "react-router"
import { connect } from "react-redux"
import { Button, Card, Checkbox, Modal } from "semantic-ui-react"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { ReactComponent as Tick } from "../../../images/tick-dynamic.svg"
import { ReactComponent as UpsellIcon } from "../../../images/common_upsell_icon.svg"
import PaymentMethod from "../../../v1/components/PaymentMethods/PaymentMethod"
import store from "../../../shared_component/utils/configureStore"
import Notifications from "../../../v1/components/Notifications"
import { createMembership } from "../../../v1/actions/membership"
import { useTranslation } from "react-i18next"
import { isMobile } from "react-device-detect"
import { BottomSheet } from "react-spring-bottom-sheet"
import AddPaymentDetails from "./AddPaymentDetails"
import { ReactComponent as PaymentCard } from "../../../images/payment-card.svg"
import {
  gaJSON,
  trackFirebaseAnalytics
} from "../../../shared_component/utils/googleAnalyticsHelpers"

const UpsellMembership = ({
  membershipData,
  braintreePaymentMethods,
  createMembership,
  history,
  location
}) => {
  const [openModal, setOpenModal] = useState(false)
  const [activated, setActivatedState] = useState(false)
  const [loading, setLoading] = useState(false)
  const activatedRef = useRef(false)

  const setActivated = (value) => {
    activatedRef.current = value
    setActivatedState(value)
  }

  useEffect(() => {
    const startTime = Date.now()

    return () => {
      const duration = Math.floor((Date.now() - startTime) / 1000)

      trackFirebaseAnalytics({
        event: gaJSON.upsell_dwell_time,
        params: {
          duration_seconds: duration,
          final_action: activatedRef.current ? "purchase" : "dismiss"
        }
      })
    }
  }, [])

  const { t } = useTranslation("tirePayment")

  const defaultPaymentMethod = braintreePaymentMethods.find((method) => method?.isdefault)

  const membership = useMemo(() => {
    if (!membershipData?.length) return null
    return [...membershipData].find((plan) => plan.name === "Premium")
  }, [membershipData])

  const [acceptTerms, setAcceptTerms] = useState(false)

  const features = [
    `${Math.trunc(membership?.percent_discount)}% discount on maintenance costs`,
    "Free quote reviews from certified mechanics"
  ].filter(Boolean)

  const upgradeMembership = async () => {
    try {
      const currentMembership = membership || {}
      currentMembership.acceptedPassengerVehicleClause = true
      currentMembership.acceptedTermsAndConditions = true
      setLoading(true)

      const res = await createMembership({
        plan: currentMembership
      })

      const data = res()
      setLoading(false)

      if (data.result) {
        setActivated(true)
        store.dispatch(
          Notifications.success({
            title: "Success",
            message: t("membership:membershipUpdateSuccessMessage"),
            position: "tr",
            autoDismiss: 5,
            action: {
              label: "Dismiss"
            }
          })
        )
        trackFirebaseAnalytics({
          event: gaJSON.premium_upsell_interaction,
          params: {
            interaction_type: "purchase"
          }
        })
      } else {
        store.dispatch(
          Notifications.error({
            title: t("membershipUpgradeModal:errorNotificationTitle"),
            message: `${data.error || t("membershipUpgradeModal:errorNotificationMessage")}`,
            position: "tr",
            autoDismiss: 5,
            action: {
              label: "Dismiss"
            }
          })
        )
        trackFirebaseAnalytics({
          event: gaJSON.upsell_payment_failure,
          params: {
            error_reason: data.error
          }
        })
      }
    } catch (error) {
      trackFirebaseAnalytics({
        event: gaJSON.upsell_payment_failure,
        params: {
          error_reason: error
        }
      })
      console.log(error)
      setLoading(false)
      setActivated(false)
    }
  }

  const goBack = () => {
    const onSuccessPath = new URLSearchParams(location.search).get("go_back")
    if (onSuccessPath) {
      history.replace(onSuccessPath)
    } else {
      history.goBack()
    }
  }

  if (activated) {
    return (
      <Card className="service-card activate-membership" raised>
        <div
          style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: "16px" }}
        >
          <img
            src="/images/green-check.png"
            width="51px"
            height="51px"
            style={{ alignSelf: "center" }}
          />
          <div>
            <p
              style={{
                color: "var(--Neutral-SB-800, #3C3C3C)",
                textAlign: "center",
                fontFamily: "Inter",
                fontSize: 18,
                fontStyle: "normal",
                fontWeight: 700,
                lineHeight: "20px",
                margin: 0
              }}
            >
              Additional savings activated
            </p>
            <p
              style={{
                color: "var(--Neutral-SB-700, #616161)",
                textAlign: "center",
                fontFamily: "Inter",
                fontSize: 14,
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "20px",
                margin: "8px 0 0 0"
              }}
            >
              Welcome to the Premium experience. Your expert support and member pricing are active.
            </p>
          </div>
        </div>
        <Button fluid className="primary-button" onClick={goBack}>
          Go back
        </Button>
      </Card>
    )
  }

  const openPaymentModal = () => {
    try {
      setOpenModal(true)
      trackFirebaseAnalytics({
        event: gaJSON.upsell_payment_interaction,
        params: {
          action: "open_add_card_form"
        }
      })
    } catch (e) {
      console.log(e)
    }
  }

  return (
    <>
      <Card className="service-card activate-membership upsell-membership" raised>
        <UpsellIcon className="upsell-icon" />

        <div className="upsell-header">
          <p className="upsell-title">Unlock your Premium savings</p>
          <p className="upsell-subtitle">Members save about $150 a year on car care.</p>
        </div>

        <div className="feature-list">
          <div className="membership-row">
            <p className="membership-label">Membership fee</p>
            <p className="price">${membership.price} / month</p>
          </div>

          {features.map((feature, i) => (
            <div className="feature-item" key={i}>
              <Tick />
              <p className="feature-text">{feature}</p>
            </div>
          ))}
        </div>

        <div className="terms-section centered">
          <Checkbox
            className="custom-orange-checkbox"
            checked={acceptTerms}
            onChange={() => setAcceptTerms(!acceptTerms)}
          />
          <p>
            I agree to the{" "}
            <a href="https://caradvise.com/terms/" target="_blank" className="terms-condition">
              terms and conditions.
            </a>
          </p>
        </div>
      </Card>

      <Card className="service-card activate-membership upsell-membership" raised>
        <div className="payment-details-section">
          <div className="payment-method-review">
            <div className="row-space-between text-start">
              <span className="payment-header">Payment method</span>
            </div>

            {defaultPaymentMethod?.token && (
              <div className="default-payment-method">
                <PaymentMethod paymentMethod={defaultPaymentMethod} hideDelete hideSetDefault />
                <span onClick={openPaymentModal} className="change-payment">
                  Change
                </span>
              </div>
            )}

            {!defaultPaymentMethod?.token && (
              <div onClick={openPaymentModal} className="no-payment-section">
                <div className="icon-circle">
                  <PaymentCard />
                </div>
                <div className="details-box">
                  <p className="text-bold-lg">Add payment method</p>
                  <p className="text-gray-sm">Credit card, PayPal, or Affirm</p>
                </div>
              </div>
            )}

            <div className="charge-warning">
              <p>You will be charged ${membership.price} immediately</p>
            </div>
          </div>
        </div>

        <div className="action_button">
          <Button
            fluid
            disabled={!acceptTerms || !defaultPaymentMethod}
            className="primary-button"
            loading={loading}
            onClick={upgradeMembership}
          >
            Join now & save!
          </Button>
        </div>
      </Card>
      <div>
        <p className="font-10">(Estimate based on typical upkeep and repairs.)</p>
      </div>
      {isMobile ? (
        <BottomSheet
          open={openModal}
          onDismiss={() => {}}
          snapPoints={({ minHeight }) => minHeight}
          draggable={false}
        >
          <AddPaymentDetails asModal={true} closeModalHandler={() => setOpenModal(false)} />
        </BottomSheet>
      ) : (
        <Modal open={openModal} className="add-payment-details-modal">
          <Modal.Content>
            <AddPaymentDetails asModal={true} closeModalHandler={() => setOpenModal(false)} />
          </Modal.Content>
        </Modal>
      )}
    </>
  )
}

function mapStateToProps(state, props) {
  const { paymentMethods, membershipData, user } = state
  const braintreePaymentMethods = Array.isArray(state?.paymentMethods?.braintreePaymentMethods)
    ? state.paymentMethods.braintreePaymentMethods.filter((method) => method?.type !== "ach")
    : []

  return {
    paymentMethods: paymentMethods.braintreePaymentMethods,
    membershipData: membershipData.availableMembershipPlans,
    activeMembershipPlan: user.membership || {},
    user: user || {},
    braintreePaymentMethods: braintreePaymentMethods
  }
}

export default withRouter(connect(mapStateToProps, { createMembership })(UpsellMembership))
